import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";
import { getApiBase } from "@/utils/api";

export interface User {
  id: string;
  username: string;
  email: string;
  full_name: string | null;
  role: "user" | "admin";
  email_verified?: boolean;
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const financeToken = ref<string | null>(localStorage.getItem("cpabuddy_finance_token"));
  const financeTokenExpires = ref<string | null>(localStorage.getItem("cpabuddy_finance_token_expires"));
  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => user.value?.role === "admin");
  // 个人财务仅对 leon 管理员开放（与后端 requireFinanceAdmin 双保险）
  const isFinanceAdmin = computed(
    () => user.value?.role === "admin" && user.value?.username === "leon"
  );

  try {
    const savedUser = localStorage.getItem("cpabuddy_user");
    if (savedUser) user.value = JSON.parse(savedUser);
  } catch {
    user.value = null;
  }

  function setUser(u: User) {
    user.value = u;
    localStorage.setItem("cpabuddy_user", JSON.stringify(u));
  }

  function clearAuth() {
    user.value = null;
    localStorage.removeItem("cpabuddy_user");
    localStorage.removeItem("cpabuddy_token");
    localStorage.removeItem("cpabuddy_finance_token");
    localStorage.removeItem("cpabuddy_finance_token_expires");
    delete axios.defaults.headers.common["Authorization"];
  }

  async function login(username: string, password: string) {
    const res = await axios.post(`${getApiBase()}/api/auth/login`, { username, password }, {
      withCredentials: true,
    });
    const data = res.data;
    const payload = data.success && data.data ? data.data : data;
    if (payload?.user) {
      setUser(payload.user);
    } else {
      throw new Error(data.message || data.detail || "登录失败");
    }
    return payload;
  }

  async function register(username: string, email: string, password: string) {
    const res = await axios.post(`${getApiBase()}/api/auth/register`, { username, email, password }, {
      withCredentials: true,
    });
    const data = res.data;
    const payload = data.success && data.data ? data.data : data;
    if (!payload?.user) {
      throw new Error(data.message || data.detail || "注册失败");
    }
    setUser(payload.user);
    return payload;
  }

  async function resendVerification(email: string) {
    const res = await axios.post(`${getApiBase()}/api/auth/resend-verification`, { email }, {
      withCredentials: true,
    });
    return res.data;
  }

  async function fetchMe() {
    const res = await axios.get(`${getApiBase()}/api/auth/me`, {
      withCredentials: true,
    });
    if (res.data) {
      const u = res.data.user || res.data;
      setUser(u);
      return u;
    }
    return null;
  }

  async function refreshToken() {
    const res = await axios.post(`${getApiBase()}/api/auth/refresh`, {}, {
      withCredentials: true,
    });
    const data = res.data;
    const payload = data.success && data.data ? data.data : data;
    if (payload?.user) {
      setUser(payload.user);
      return payload.user;
    }
    throw new Error(data.message || "刷新失败");
  }

  async function fetchFinanceToken() {
    const res = await axios.post(
      `${getApiBase()}/api/finance/token`,
      {},
      { withCredentials: true }
    );
    const data = res.data.data || res.data;
    if (!data?.access_token) {
      throw new Error("无法获取金融工具授权");
    }
    financeToken.value = data.access_token;
    financeTokenExpires.value = data.expires_at;
    localStorage.setItem("cpabuddy_finance_token", data.access_token);
    localStorage.setItem("cpabuddy_finance_token_expires", data.expires_at);
    return data.access_token as string;
  }

  function isFinanceTokenValid() {
    if (!financeToken.value) return false;
    if (!financeTokenExpires.value) return true;
    return new Date(financeTokenExpires.value).getTime() > Date.now() + 5 * 60 * 1000;
  }

  async function getFinanceToken() {
    if (isFinanceTokenValid() && financeToken.value) return financeToken.value;
    return fetchFinanceToken();
  }

  async function logout() {
    try {
      await axios.post(`${getApiBase()}/api/auth/logout`, {}, {
        withCredentials: true,
      });
    } catch {
      // ignore logout errors
    } finally {
      clearAuth();
    }
  }

  return {
    user,
    isAuthenticated,
    isAdmin,
    isFinanceAdmin,
    login,
    register,
    resendVerification,
    fetchMe,
    refreshToken,
    logout,
    setUser,
    clearAuth,
    fetchFinanceToken,
    getFinanceToken,
    isFinanceTokenValid,
  };
});
